/**
 * Validation functions for simulation configuration
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5
 */

import { GridType, Topology, type SimulationConfig } from './types.js';
import { getDirectionCount } from './grid-navigator.js';

/**
 * Maximum allowed grid dimension
 */
export const MAX_GRID_SIZE = 5000;

/**
 * Validation result interface
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Error messages for validation failures
 */
export const ERROR_MESSAGES = {
  RULE_SET_LENGTH: 'Rule set length must match color count',
  TURN_VALUE_RANGE: (max: number) => `Turn values must be between 0 and ${max - 1}`,
  GRID_SIZE_EXCEEDED: `Grid dimensions must not exceed ${MAX_GRID_SIZE}`,
  GRID_SIZE_POSITIVE: 'Grid dimensions must be positive integers',
  POSITION_OUT_OF_BOUNDS: 'Starting position must be within grid bounds',
  INVALID_TOPOLOGY: 'Triangular and hexagonal grids only support torus topology',
  COLORS_REQUIRED: 'At least one color is required',
  RULE_SET_REQUIRED: 'Rule set is required',
};

/**
 * Validates a simulation configuration
 * 
 * @param config - Partial simulation configuration to validate
 * @returns ValidationResult with valid flag and array of error messages
 * 
 * Requirements validated:
 * - 7.1: Rule set length must match color count
 * - 7.2: Turn values must be within [0, directionCount-1]
 * - 7.3: Grid dimensions must not exceed MAX_GRID_SIZE
 * - 7.4: Starting position must be within grid bounds
 * - 7.5: Non-square grids only support torus topology
 */
export function validateSimulationConfig(
  config: Partial<SimulationConfig>
): ValidationResult {
  const errors: string[] = [];

  // 7.3: Grid dimensions validation
  if (config.width !== undefined) {
    if (config.width <= 0 || !Number.isInteger(config.width)) {
      errors.push(ERROR_MESSAGES.GRID_SIZE_POSITIVE);
    } else if (config.width > MAX_GRID_SIZE) {
      errors.push(ERROR_MESSAGES.GRID_SIZE_EXCEEDED);
    }
  }

  if (config.height !== undefined) {
    if (config.height <= 0 || !Number.isInteger(config.height)) {
      errors.push(ERROR_MESSAGES.GRID_SIZE_POSITIVE);
    } else if (config.height > MAX_GRID_SIZE) {
      errors.push(ERROR_MESSAGES.GRID_SIZE_EXCEEDED);
    }
  }

  // 7.1: Rule set length must match color count
  if (config.colors !== undefined && config.ruleSet !== undefined) {
    if (config.colors.length === 0) {
      errors.push(ERROR_MESSAGES.COLORS_REQUIRED);
    }
    if (config.ruleSet.length !== config.colors.length) {
      errors.push(ERROR_MESSAGES.RULE_SET_LENGTH);
    }
  }

  // 7.2: Turn values must be within grid type's direction count
  if (config.gridType !== undefined && config.ruleSet !== undefined) {
    const maxDir = getDirectionCount(config.gridType);
    const hasInvalidTurn = config.ruleSet.some(
      turn => turn < 0 || turn >= maxDir || !Number.isInteger(turn)
    );
    if (hasInvalidTurn) {
      errors.push(ERROR_MESSAGES.TURN_VALUE_RANGE(maxDir));
    }
  }

  // 7.4: Starting position must be within grid bounds
  if (
    config.startPosition !== undefined &&
    config.width !== undefined &&
    config.height !== undefined
  ) {
    const { x, y } = config.startPosition;
    if (
      x < 0 ||
      x >= config.width ||
      y < 0 ||
      y >= config.height ||
      !Number.isInteger(x) ||
      !Number.isInteger(y)
    ) {
      errors.push(ERROR_MESSAGES.POSITION_OUT_OF_BOUNDS);
    }
  }

  // 7.5: Non-square grids only support torus topology
  if (config.gridType !== undefined && config.topology !== undefined) {
    if (
      config.gridType !== GridType.Square &&
      config.topology !== Topology.Torus
    ) {
      errors.push(ERROR_MESSAGES.INVALID_TOPOLOGY);
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
